import { I18n } from '@calendly/i18n';
import { AlertWarningTriangleIcon } from '@calendly/ui/components/icons';
import { BodyText } from '@calendly/ui/components/typography';
import { colorTextDestructive, colorTextSecondary, primaryColorL1, colorTextActionContrast } from '@calendly/ui/theme';
import React from 'react';
import { MeetingStatusIndicator } from 'components/meetings/MeetingStatusIndicator';
import { DisplayStatus } from 'components/meetings/utils';

/**
 * Routes between LegacyStatusBadge and MeetingStatusIndicator based on realtime feature flag.
 *
 * Transitional component — see `useMeetingLiveStatus` for the full feature flag removal guide.
 * This file, `LegacyStatusBadge`, `BADGE_CONFIG`, and `COLOR_MAP` should all be deleted
 * when `meetings_recaps_realtime_feature` is removed.
 */

const bodyWithIconCss = "b29rjvk";
type BadgeColor = 'destructive' | 'secondary' | 'action';
interface BadgeConfigEntry {
  color: BadgeColor;
  icon: boolean;
  key: string;
}
const BADGE_CONFIG: Record<string, BadgeConfigEntry> = {
  PAYMENT_ERROR: {
    color: 'destructive',
    icon: true,
    key: 'payment_error'
  },
  ACTION_NEEDED: {
    color: 'destructive',
    icon: true,
    key: 'action_needed'
  },
  EXPIRED: {
    color: 'destructive',
    icon: false,
    key: 'expired'
  },
  RESCHEDULED: {
    color: 'secondary',
    icon: false,
    key: 'rescheduled'
  },
  CONFLICT: {
    color: 'destructive',
    icon: false,
    key: 'conflict'
  },
  CANCELLED: {
    color: 'destructive',
    icon: false,
    key: 'canceled'
  },
  NO_SHOW: {
    color: 'destructive',
    icon: false,
    key: 'no_show'
  },
  INVITEES_DECLINED: {
    color: 'destructive',
    icon: true,
    key: 'invitees_declined'
  },
  IN_PROGRESS: {
    color: 'action',
    icon: false,
    key: 'in_progress'
  },
  UP_NEXT: {
    color: 'action',
    icon: false,
    key: 'up_next'
  }
};
const COLOR_MAP: Record<BadgeColor, (shouldUseContrast?: boolean) => string> = {
  destructive: () => colorTextDestructive,
  secondary: () => colorTextSecondary,
  action: shouldUseContrast => shouldUseContrast ? colorTextActionContrast : primaryColorL1
};
interface LegacyStatusBadgeProps {
  badgeKey: string;
  shouldUseContrast: boolean;
}
const LegacyStatusBadge = ({
  badgeKey,
  shouldUseContrast
}: LegacyStatusBadgeProps) => {
  const config = BADGE_CONFIG[badgeKey];
  if (!config) return null;
  const color = COLOR_MAP[config.color](shouldUseContrast);
  const label = I18n.t(`publisher.meetings.header_badge.${config.key}`);
  const isAction = config.color === 'action';
  return <BodyText color={color} weight="semibold" size="small" {...isAction && {
    as: 'div'
  }}>
      {config.icon ? <div className={bodyWithIconCss}>
          <AlertWarningTriangleIcon />
          {label}
        </div> : label}
    </BodyText>;
};
interface MeetingListLiveStatusProps {
  liveStatus: {
    hasRealtimeFeatures: boolean;
    realtimeStatus?: DisplayStatus;
    displayText?: string;
    badgeKey?: string;
  };
  shouldUseContrast: boolean;
}

/**
 * Routes between the legacy status badge and the realtime status indicator.
 *
 * @remarks
 * **Temporary component** — will be deleted when the `meetings_recaps_realtime_feature`
 * flag is removed. See `useMeetingLiveStatus` for the full removal guide.
 *
 * When `hasRealtimeFeatures` is true, renders `MeetingStatusIndicator` with
 * the live status from `MeetingsContext`. Otherwise renders `LegacyStatusBadge`
 * using the badge key derived from `getBadgeKey`.
 *
 * @param liveStatus - Output from `useMeetingLiveStatus`, containing the active path's data
 * @param shouldUseContrast - Whether the legacy badge should use high-contrast styling
 */
export const MeetingListLiveStatus = ({
  liveStatus,
  shouldUseContrast
}: MeetingListLiveStatusProps) => {
  if (liveStatus.hasRealtimeFeatures) {
    return <MeetingStatusIndicator status={liveStatus.realtimeStatus} displayText={liveStatus.displayText} />;
  }
  return <LegacyStatusBadge badgeKey={liveStatus.badgeKey} shouldUseContrast={shouldUseContrast} />;
};

require("./MeetingListLiveStatus.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./MeetingListLiveStatus.tsx");